// ID copies are kept small at upload, so the advocate's single PDF stays small enough to email.
import { optimizePdfToMaxBytes } from './pdfReducer';

const MAX_PDF_BYTES = 2 * 1024 * 1024;
const MAX_SIDE = 1600;

export const TOO_SMALL_MESSAGE = 'This photo is too small to read. Please take it again, closer and in good light.';
/** Below this, numbers on an ID can't be read. */
export const isTooSmall = (width: number, height: number, minSide: number) => Math.max(width, height) < minSide;

export const LOCKED_PDF_MESSAGE = 'This PDF is password protected. Please upload a photo or an unlocked copy.';

/** e-Aadhaar PDFs usually carry a password; those can't be joined into the advocate's PDF. */
export const isLockedPdf = (bytes: Uint8Array) => {
  let text = '';
  for (let i = 0; i < bytes.length; i += 0x8000) text += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)));
  return /\/Encrypt\s+\d+\s+\d+\s+R|\/Encrypt\s*<</.test(text);
};

const mb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** A smaller JPEG for photos, a checked (and if needed reduced) PDF, or an error message to show. */
export async function shrinkIdFile(file: File, opts: { minSide?: number } = {}): Promise<File> {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (isPdf) {
    if (isLockedPdf(new Uint8Array(await file.arrayBuffer()))) throw new Error(LOCKED_PDF_MESSAGE);
    if (file.size <= MAX_PDF_BYTES) return file;
    const res = await optimizePdfToMaxBytes({ file, maxBytes: MAX_PDF_BYTES, label: file.name, formatBytes: mb });
    return res.file;
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('This photo could not be opened. Please upload a JPG or PNG.'));
      el.src = url;
    });
    if (opts.minSide && isTooSmall(img.naturalWidth, img.naturalHeight, opts.minSide)) throw new Error(TOO_SMALL_MESSAGE);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('This photo could not be prepared.'))), 'image/jpeg', 0.82));
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(url);
  }
}
